import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import Avatar from '../ui/Avatar'
import {
  Empty,
  IconEdit,
  IconPlus,
  IconRefresh,
  Modal,
  Note,
  Pill,
  Search,
  TableSkeleton,
  haystack,
  useLatest,
  useTableStyles
} from './peopleUI'
import './people.css'

/*
 * Admin Users — who can sign in to this back-office.
 *
 * The API is exactly what it was: GET admin/adminusers and POST admin/adminusers
 * with the whole record. Nothing here changes a URL or a payload.
 *
 * Three things are new to the screen and none of them touch the server:
 *   - a keyword filter over the rows already in memory
 *   - a sticky-header table inside its own scroller
 *   - the save result is an inline note instead of an alert() box
 *
 * A word on what is deliberately absent: the old file carried a deleteRecord()
 * that was never wired to a control and pointed at DELETE admin/sports/:id —
 * the sports endpoint, not the admin-users one. Reviving it would have deleted
 * a sport. It is gone rather than quietly kept.
 */

const ADMIN_UN = 'admin'

export default function AdminUsers() {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [show, setShow] = useState(false)
  const [record, setRecord] = useState({})

  const [q, setQ] = useState('')
  const [note, setNote] = useState(null)
  const [failed, setFailed] = useState(false)
  const [saving, setSaving] = useState(false)

  const tableStyles = useTableStyles(false)

  /* The column array below is memoised once, so showModal() closes over the
     first render's `data` — which is []. It reads the rows through this ref
     instead. */
  const dataRef = useLatest(data)

  const columns = useMemo(() => [
    {
      name: 'Name',
      selector: (row) => row.UN || '',
      cell: (row) => (
        <div className="pp-face">
          <Avatar name={row.UN} size={32} />
          <div className="pp-cell">
            <button
              type="button"
              className="pp-name pp-cell-line"
              title="Edit"
              onClick={() => showModal(row.id)}
            >
              {row.UN || '—'}
            </button>
            {row.UN === ADMIN_UN ? <span className="pp-cell-sub">Primary account</span> : null}
          </div>
        </div>
      ),
      sortable: true,
      minWidth: '220px',
      grow: 2
    },
    {
      name: 'Email',
      selector: (row) => row.Email || '',
      cell: (row) => (row.Email ? row.Email : <span className="pp-dash">—</span>),
      sortable: true,
      minWidth: '220px',
      grow: 2
    },
    {
      name: 'Role',
      selector: (row) => (row.UN === ADMIN_UN ? 0 : 1),
      cell: (row) => (
        row.UN === ADMIN_UN
          ? <Pill tone="accent">ADMIN</Pill>
          : <Pill tone="flat">STAFF</Pill>
      ),
      sortable: true,
      width: '120px'
    },
    {
      name: '',
      cell: (row) => (
        <button
          type="button"
          className="pp-rowbtn"
          title="Edit"
          aria-label={'Edit ' + (row.UN || 'admin user')}
          onClick={() => showModal(row.id)}
        >
          <i><IconEdit /></i>
        </button>
      ),
      width: '60px',
      center: true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [])

  const handleClose = () => setShow(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = () => {
    setIsLoading(true)
    setFailed(false)
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/adminusers', {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setData(res.data.results)
        setIsLoading(false)
      }).catch(err => {
        setIsLoading(false)
        setFailed(true)
        console.log(err)
      })
  }

  const showModal = (id) => {
    setNote(null)
    if (id == 0) {
      setRecord({ ModalTitle: 'NEW RECORD', id: 0, UN: '', Email: '', PWD: '' })
      setShow(true)
    } else {
      const recObj = (dataRef.current || []).filter(item => item.id == id)[0]
      if (!recObj) return
      setRecord({ ...recObj, ModalTitle: recObj.UN })
      setShow(true)
    }
  }

  const save = (e) => {
    e.preventDefault()
    setSaving(true)

    axios.post(process.env.REACT_APP_API_SERVER + 'admin/adminusers', record, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setSaving(false)
        if (res.data.err == 0) {
          loadData()
        }
        setShow(false)
        setNote({ tone: res.data.err == 0 ? 'good' : 'crit', text: res.data.msg })
      }).catch(err => {
        setSaving(false)
        setNote({ tone: 'crit', text: 'The save did not go through, please try again' })
      })
  }

  const rows = Array.isArray(data) ? data : []

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return rows
    return rows.filter((r) => haystack([r.UN, r.Email]).includes(needle))
  }, [rows, q])

  const pwd = record.PWD === undefined || record.PWD === null ? '' : String(record.PWD)
  const pwd2 = record.PWD2 === undefined || record.PWD2 === null ? '' : String(record.PWD2)
  const mismatch = pwd2.length > 0 && pwd !== pwd2

  return (
    <div className="pp-stack">
      {note ? (
        <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>
      ) : null}

      {failed ? (
        <Note tone="crit" onDismiss={() => setFailed(false)}>
          The admin users list could not be loaded. Please try again.
        </Note>
      ) : null}

      <Panel
        title="Admin Users"
        subtitle={
          isLoading
            ? 'Loading…'
            : rows.length + (rows.length === 1 ? ' account' : ' accounts')
        }
        actions={
          <>
            <Btn onClick={loadData} title="Refresh" aria-label="Refresh">
              <IconRefresh />
            </Btn>
            <Btn variant="accent" onClick={() => showModal(0)}>
              <IconPlus /> New record
            </Btn>
          </>
        }
      >
        {isLoading ? (
          <TableSkeleton rows={4} />
        ) : rows.length === 0 ? (
          <Empty
            title="No admin users"
            action={<Btn variant="accent" onClick={() => showModal(0)}><IconPlus /> New record</Btn>}
          >
            The accounts created here are the ones that can sign in
            to the back-office.
          </Empty>
        ) : (
          <>
            {rows.length > 6 ? (
              <div className="pp-toolbar">
                <Search
                  value={q}
                  onChange={setQ}
                  placeholder="Search by name or email…"
                  label="Search admin users"
                />
                <span className="pp-toolbar-spacer" />
                <span className="pp-count"><b>{shown.length}</b> of {rows.length}</span>
              </div>
            ) : null}

            {shown.length === 0 ? (
              <Empty
                title="No results"
                action={<Btn onClick={() => setQ('')}>Clear search</Btn>}
              >
                No account matches “{q.trim()}”.
              </Empty>
            ) : (
              <div className="pp-table">
                <DataTable
                  columns={columns}
                  data={shown}
                  customStyles={tableStyles}
                  highlightOnHover
                  persistTableHead
                  fixedHeader
                  fixedHeaderScrollHeight="62vh"
                  noDataComponent={<Empty title="No admin users" />}
                />
              </div>
            )}
          </>
        )}
      </Panel>

      <Modal
        open={show}
        title={record.ModalTitle}
        subtitle={record.id > 0 ? 'Edit account' : 'New back-office account'}
        onClose={handleClose}
      >
        <form className="pp-form" onSubmit={save} id="pp-adminuser-form">
          <div className="pp-grid">
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="pp-au-un">User Name</label>
              <input
                id="pp-au-un"
                className="pp-input"
                disabled={record.UN == ADMIN_UN}
                type="text"
                required
                autoComplete="off"
                value={record.UN === undefined || record.UN === null ? '' : record.UN}
                onChange={(e) => setRecord({ ...record, UN: e.target.value })}
              />
              {record.UN == ADMIN_UN ? (
                <div className="pp-hint">The primary account’s username cannot be changed.</div>
              ) : null}
            </div>

            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="pp-au-email">Email</label>
              <input
                id="pp-au-email"
                className="pp-input"
                type="email"
                required
                autoComplete="off"
                value={record.Email === undefined || record.Email === null ? '' : record.Email}
                onChange={(e) => setRecord({ ...record, Email: e.target.value })}
              />
            </div>
          </div>

          <fieldset className="pp-fieldset">
            <legend className="pp-legend">Password</legend>
            <div className="pp-grid">
              <div className="pp-field">
                <label className="pp-label" data-req="1" htmlFor="pp-au-pwd">Password</label>
                <input
                  id="pp-au-pwd"
                  className="pp-input"
                  minLength={6}
                  maxLength={10}
                  type="password"
                  required
                  autoComplete="new-password"
                  value={pwd}
                  onChange={(e) => setRecord({ ...record, PWD: e.target.value })}
                />
                <div className="pp-hint">6 to 10 characters.</div>
              </div>

              <div className="pp-field">
                <label className="pp-label" data-req="1" htmlFor="pp-au-pwd2">Confirm Password</label>
                <input
                  id="pp-au-pwd2"
                  className="pp-input"
                  minLength={6}
                  maxLength={10}
                  type="password"
                  required
                  autoComplete="new-password"
                  aria-invalid={mismatch ? 'true' : undefined}
                  value={pwd2}
                  onChange={(e) => setRecord({ ...record, PWD2: e.target.value })}
                />
                {mismatch ? (
                  <div className="pp-hint" data-tone="crit">The two passwords do not match.</div>
                ) : null}
              </div>
            </div>
          </fieldset>

          <div className="pp-actions">
            <Btn onClick={handleClose} type="button">Cancel</Btn>
            <Btn variant="accent" type="submit" disabled={saving || record.PWD != record.PWD2}>
              {saving ? 'Saving…' : 'Save'}
            </Btn>
          </div>
        </form>
      </Modal>
    </div>
  )
}
